import { getLanguage } from "CMS/utils";
import { InnerFormTemplate } from "components/MainSectionTemplate/InnerFormTemplate";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { useValidateIsNotEmptyArray } from "formvalidation/hooks";
import { Message } from "semantic-ui-react";
import React from "react";
import { sortAndPadTranslationValues, sortTranslationValues } from "utilities/freefieldUtils";
import { replaceNewLineWithBr } from "utilities/reactUtils";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { EditNewSectionField } from "components/MainSectionTemplate/types";

interface TranslationValueInnerFormProps {
  values: server.dto.TranslationValue[];
  onChange: (values: server.dto.TranslationValue[]) => void;
  submit: () => void;
  isSaving: boolean;
  renderInputForValue: (value: server.dto.TranslationValue, onChange: (value: server.dto.TranslationValue) => void, isFirst: boolean) => React.ReactNode;
}

const TranslationValueInnerForm = (props: TranslationValueInnerFormProps) => {
  const onChange = (newValue: server.dto.TranslationValue) => {
    const newValues = getNewTranslationValues(newValue, props.values);
    props.onChange(newValues);
  }

  return <InnerFormTemplate
    submit={props.submit}
    isSaving={props.isSaving}
    section={{
      key: "translationValues",
      title: "",
      type: "regular",
      fields: sortAndPadTranslationValues(props.values).map((value, index) => ({
        key: `translationValue-${index}`,
        label: getLanguage(value.language).twoLetterTag.toUpperCase(),
        renderEditNew: () => props.renderInputForValue(value, onChange, index === 0),
      }))
    }}
  />
}

export const RequiredTranslationValueInput = (props: Omit<TranslationValueInnerFormProps, "renderInputForValue">) => {
  return <TranslationValueInnerForm
    {...props}
    renderInputForValue={(value, onChange, isFirst) =>
      <RequiredTextInput
        value={value.value}
        onChange={v => onChange({ ...value, value: v })}
        focusOnMount={isFirst}
        maxLength={serverReference.constants.bookingRequest.maxTitleInternalLength}
        validatorKey={`translationValue-${value.language}`}
      />
    }
  />;
}

export const OptionalTranslationValueInput = (props: Omit<TranslationValueInnerFormProps, "renderInputForValue">) => {
  return <TranslationValueInnerForm
    {...props}
    renderInputForValue={(value, onChange, isFirst) =>
      <OptionalTextInput
        value={value.value}
        onChange={v => onChange({ ...value, value: v })}
        focusOnMount={isFirst}
        maxLength={serverReference.constants.bookingRequest.maxTitleInternalLength}
      />
    }
  />;
}

export const OptionalTextAreaTranslationValueInput = (props: Omit<TranslationValueInnerFormProps, "renderInputForValue">) => {
  return <TranslationValueInnerForm
    {...props}
    renderInputForValue={(value, onChange) =>
      <OptionalTextAreaInput
        rows={3}
        value={value.value}
        onChange={v => onChange({ ...value, value: v })}
        onCtrlEnter={props.submit}
        focusOnMount={value.language === "english"} // Focus the English field, since that's the first one
        maxLength={serverReference.constants.bookingRequest.maxNoteLength}
      />
    }
  />;
}

interface AtLeastOneTranslationValueInputProps extends Omit<TranslationValueInnerFormProps, "renderInputForValue"> {
  validatorKey: string;
}

export const AtLeastOneTranslationValueInput = (props: AtLeastOneTranslationValueInputProps) => {
  const validator = useValidateIsNotEmptyArray({
    key: props.validatorKey,
    title: props.validatorKey,
    value: props.values,
  });

  return <>
    {validator.error.hasError && <Message className="margin-bottom" color="red">
      At least one translation value is required.
    </Message>}
    <OptionalTextAreaTranslationValueInput
      {...props}
    />
  </>
}

export const TranslationValueReadonly = (props: { values: server.dto.TranslationValue[] }) => {
  return <div className="flex-column gap-small">
    {sortTranslationValues(props.values).map(value => (
      <div key={value.language} className="flex gap">
        <div style={{ opacity: 0.7, width: 18 }}>{getLanguage(value.language).twoLetterTag.toUpperCase()}</div>
        <div className="grow1" title={value.value}>
          {replaceNewLineWithBr(value.value)}
        </div>
      </div>
    ))}
  </div>
}

interface TranslationValueForNewState {
  values: server.dto.TranslationValue[];
  onChange: (newValue: server.dto.TranslationValue) => void;
  onFocus: () => void;
}

export const useTranslationValuesForNewState = (initialValues: server.dto.TranslationValue[], onChanged: (values: server.dto.TranslationValue[]) => void): TranslationValueForNewState => {
  const [values, setValues] = React.useState<server.dto.TranslationValue[]>(initialValues);
  const [shouldEditAll, setShouldEditAll] = React.useState<boolean>(false);

  const onChange = (newValue: server.dto.TranslationValue) => {
    if (shouldEditAll) {
      const newValues = sortAndPadTranslationValues(values).map(v => ({ ...v, value: newValue.value }));
      setValues(newValues);
      onChanged(newValues);
    } else {
      const newValues = getNewTranslationValues(newValue, values);
      setValues(newValues);
      onChanged(newValues);
    }
  }

  const onFocus = () => {
    const sortedAndPadded = sortAndPadTranslationValues(values);
    const areAllEmpty = sortedAndPadded.every(v => v.value.length === 0);
    if (areAllEmpty) {
      setShouldEditAll(true);
    } else {
      setShouldEditAll(false);
    }
  }

  return { values, onChange, onFocus };
}

const getTranslationValueFieldsForNew = (
  key: string,
  state: TranslationValueForNewState,
  renderValue: (value: server.dto.TranslationValue, onChange: (value: server.dto.TranslationValue) => void, onFocus: () => void) => React.ReactNode,
): EditNewSectionField[] => sortAndPadTranslationValues(state.values).map((value, index) => ({
  key: `${key}-translationValue-${index}`,
  label: getLanguage(value.language).twoLetterTag.toUpperCase(),
  renderEditNew: () => renderValue(value, state.onChange, state.onFocus)
}));

export const getRequiredTranslationValueFieldsForNew = (key: string, state: TranslationValueForNewState): EditNewSectionField[] =>
  getTranslationValueFieldsForNew(key, state, (value, onChange, onFocus) =>
    <RequiredTextInput
      validatorKey={`${key}-translationValue-${value.language}`}
      value={value.value}
      onChange={v => onChange({ ...value, value: v })}
      onFocus={onFocus}
      maxLength={serverReference.constants.freeFieldDefinition.maxTitleLength}
    />);

export const getOptionalTranslationValueFieldsForNew = (key: string, state: TranslationValueForNewState): EditNewSectionField[] =>
  getTranslationValueFieldsForNew(key, state, (value, onChange, onFocus) =>
    <OptionalTextInput
      value={value.value}
      onChange={v => onChange({ ...value, value: v })}
      onFocus={onFocus}
      maxLength={serverReference.constants.freeFieldDefinition.maxTitleLength}
    />);

const getNewTranslationValues = (newValue: server.dto.TranslationValue, values: server.dto.TranslationValue[]): server.dto.TranslationValue[] => {
  const newValues = [...values];
  const index = newValues.findIndex(v => v.language === newValue.language);
  if (index !== -1) {
    newValues[index] = newValue;
  } else {
    newValues.push(newValue);
  }
  return newValues.filter(v => v.value.length > 0);
}